import React from 'react';
import { Link } from 'react-router-dom';
import { ArrowRight, MapPin, ShieldCheck, Stethoscope, BadgeCheck } from 'lucide-react';
import PageHero from '../components/PageHero';
import Reveal from '../components/Reveal';
import SEO, { webPageSchema } from '../components/SEO';
import CTASection from '../components/CTASection';
import { BRAND_NAME } from '../utils/brand';

// /clinics — SEO landing page for "weight loss clinic kuala lumpur".
// Marketing detail on the panel-clinic network. The actual per-clinic
// browse UI lives inside the patient dashboard (/clinics after login).

const VALUE_POINTS = [
  {
    icon: BadgeCheck,
    title: 'MMC-licensed clinicians',
    body: 'Every partner clinic is staffed by Malaysia Medical Council–licensed doctors with active Annual Practising Certificates.',
  },
  {
    icon: ShieldCheck,
    title: 'In-person consultations',
    body: 'Programme consultations happen face-to-face at the clinic — never online. This is a legal requirement for controlled therapies in Malaysia.',
  },
  {
    icon: Stethoscope,
    title: 'Doctor-led care pathway',
    body: 'Your assessment is reviewed by a licensed doctor before any treatment plan is discussed. AI supports the workflow; doctors make clinical decisions.',
  },
  {
    icon: MapPin,
    title: 'Klang Valley network',
    body: 'Panel clinics across Kuala Lumpur, Petaling Jaya, Subang, Cheras, Puchong and Damansara. New locations added as demand grows.',
  },
];

export default function Clinics() {
  return (
    <>
      <SEO jsonLd={webPageSchema(
        '/clinics',
        'Panel Clinics & Doctors | Medical Weight Loss Kuala Lumpur | MyDr.Ai',
        'Licensed panel clinics and medical doctors across the Klang Valley delivering in-person MyDr.Ai medical weight management consultations.'
      )} />

      <PageHero
        eyebrow="Panel clinics"
        title="Licensed Panel Clinics & Medical Doctors Across Klang Valley"
        subtitle={`Every ${BRAND_NAME} consultation is an in-person, face-to-face visit with an MMC-licensed doctor at one of our partner clinics.`}
      />

      <section className="bg-white">
        <div className="container-x py-12 sm:py-16">
          <Reveal>
            <div className="grid sm:grid-cols-2 gap-5">
              {VALUE_POINTS.map((p) => (
                <div key={p.title} className="card-soft p-6">
                  <div className="w-11 h-11 rounded-xl bg-brand-50 text-brand-700 flex items-center justify-center">
                    <p.icon size={20} />
                  </div>
                  <h2 className="mt-4 text-xl font-semibold text-ink-900">{p.title}</h2>
                  <p className="mt-2 text-sm text-ink-600 leading-relaxed">{p.body}</p>
                </div>
              ))}
            </div>
          </Reveal>

          <Reveal>
            <div className="mt-10 card-soft p-7 sm:p-10">
              <h2 className="text-2xl font-bold tracking-tight text-ink-900">
                How clinic matching works
              </h2>
              <p className="mt-3 text-ink-600">
                Complete a short eligibility assessment. If suitable for programme
                enrolment, the platform suggests panel clinics near you based on
                location and current availability. You choose the clinic and the
                appointment slot; a licensed doctor at the clinic will review your
                case in person.
              </p>
              <div className="mt-6 flex flex-wrap gap-3">
                <Link to="/how-it-works" className="btn-outline">
                  See the full process
                </Link>
                <Link to="/intake" className="btn-brand">
                  Am I Qualified? <ArrowRight size={16} />
                </Link>
              </div>
            </div>
          </Reveal>
        </div>
      </section>

      <CTASection />
    </>
  );
}
